import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import RealtimeConversation from "../RealtimeConversation";
import { auth } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import {
  createInviteInterview,
  getInviteJob,
  getJobBranding,
  JOB_MODES,
  updateInviteInterviewStatus
} from "../services/jobService";
import { CompanyMcqTest, CompanyCodingTest } from "./CompanyAssessmentRoute";
import useDocumentTitle, { useHideFavicon } from "../hooks/useDocumentTitle";
import "./RecruiterJobs.css";

const initialCandidate = {
  name: "",
  email: "",
  experience: ""
};

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

export default function JobInterviewRoute({ AppBackground, AppHeader }) {
  const { jobId } = useParams();
  const { user, authLoading, handleLogout } = useAuth();
  const [job, setJob] = useState(null);
  const [branding, setBranding] = useState(null);
  const [candidate, setCandidate] = useState(initialCandidate);
  const [candidateUser, setCandidateUser] = useState(null);
  const [interviewConfig, setInterviewConfig] = useState(null);
  const [interviewId, setInterviewId] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);
  const [preparingGreeting, setPreparingGreeting] = useState(false);
  const [prepareStatus, setPrepareStatus] = useState("");
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const userRole = user?.role || user?.claims?.role || "INTERVIEWEE";
  const isCandidateAccount = userRole === "INTERVIEWEE";
  useDocumentTitle(branding?.name?.trim());
  const coBranded = !!(branding?.name?.trim() || branding?.logo);
  useHideFavicon(coBranded);
  // Header tagline is just the test name — for every job link, branded or not.
  const jobTagline = (j) => j.title;

  useEffect(() => {
    if (!user) {
      return;
    }

    setCandidate((current) => ({
      ...current,
      name: current.name || user.displayName || "",
      email: user.email || current.email || ""
    }));
    setCandidateUser(user);
  }, [user]);

  useEffect(() => {
    let active = true;

    async function loadJob() {
      try {
        setLoading(true);
        setError("");
        // getJobBranding never throws — no branding just means the plain Vagmee header.
        const [result, brand] = await Promise.all([getInviteJob(jobId), getJobBranding(jobId)]);

        if (!active) {
          return;
        }
        setBranding(brand);

        if (!result) {
          setError("This interview link was not found.");
          return;
        }

        if (result.status !== "ACTIVE") {
          setError("This interview link is inactive.");
          return;
        }

        setJob(result);
      } catch (err) {
        console.error("Failed to load job interview:", err);
        setError(err.message || "Unable to load this interview link.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadJob();

    return () => {
      active = false;
    };
  }, [jobId]);

  if (!jobId) {
    return <Navigate to="/" replace />;
  }

  if (authLoading) {
    return (
      <div className="app-container">
        <AppBackground />
        <div className="loading-screen">
          <p>Checking login...</p>
        </div>
      </div>
    );
  }

  if (!authLoading && !user) {
    return <Navigate to="/login" replace state={{ returnTo: `/interview/${jobId}` }} />;
  }

  const updateCandidate = (field, value) => {
    setCandidate((current) => ({ ...current, [field]: value }));
  };

  const validateCandidate = () => {
    if (!candidate.name.trim()) {
      return "Name is required.";
    }
    if (!candidate.email.trim()) {
      return "Email is required.";
    }
    if (!/^\S+@\S+\.\S+$/.test(candidate.email.trim())) {
      return "Enter a valid email address.";
    }
    if (!candidate.experience.trim()) {
      return "Experience is required.";
    }
    return "";
  };

  const buildGreetingText = (name, role) => (
    `Hello ${name}, welcome to your ${role} interview. Let's begin.`
  );

  const pollGreeting = async (sessionId, token, fallbackText) => {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const response = await fetch(`${API_BASE}/interview/session/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Greeting status failed with HTTP ${response.status}`);
      }

      const data = await response.json();
      const greeting = data.greeting || {};

      if (greeting.status === "READY") {
        return {
          status: "READY",
          text: fallbackText,
          audio_url: greeting.audio_url,
          duration: greeting.duration,
          timeline: greeting.timeline || []
        };
      }

      if (greeting.status === "FAILED") {
        throw new Error(greeting.error || "Greeting generation failed.");
      }

      setPrepareStatus("Preparing your greeting...");
    }

    throw new Error("Greeting preparation timed out. Please try again.");
  };

  const prepareInterviewSession = async (baseConfig, signedInUser) => {
    setPreparingGreeting(true);
    setPrepareStatus("Preparing your interview...");

    try {
      const token = await signedInUser.getIdToken();
      const greetingText = buildGreetingText(baseConfig.candidate_name, baseConfig.role);
      const formData = new FormData();

      formData.append("username", baseConfig.candidate_name);
      formData.append("role", baseConfig.role);
      formData.append("years_experience", baseConfig.years_experience);
      formData.append("difficulty", baseConfig.difficulty);
      formData.append("job_id", baseConfig.job_id);
      formData.append("b2b_interview_mode", baseConfig.b2b_interview_mode);

      if (baseConfig.uploaded_questions?.length) {
        formData.append("uploaded_questions", JSON.stringify(baseConfig.uploaded_questions));
      }

      const response = await fetch(`${API_BASE}/get_greeting`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Interview preparation failed with HTTP ${response.status}`);
      }

      const data = await response.json();
      let greeting = data.greeting || {};

      if (greeting.status === "READY") {
        greeting = {
          ...greeting,
          text: greeting.text || greetingText,
          timeline: greeting.timeline || []
        };
      } else {
        setPrepareStatus("Preparing your greeting...");
        greeting = await pollGreeting(data.session_id, token, greetingText);
      }

      setInterviewConfig({
        ...baseConfig,
        session_id: data.session_id,
        greeting,
        total_questions: data.total_questions || 10,
        is_trial: !!data.is_trial
      });
      setPrepareStatus("Starting interview...");
      setInterviewStarted(true);
    } catch (err) {
      console.error("Failed to prepare invite interview:", err);
      setError(err.message || "Unable to prepare the interview.");
      setPrepareStatus("");
    } finally {
      setPreparingGreeting(false);
    }
  };

  const startInterview = async (event) => {
    event.preventDefault();
    const validationError = validateCandidate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setStarting(true);
      setError("");

      const invite = await createInviteInterview({
        jobId: job.jobId,
        candidateName: candidate.name.trim(),
        candidateEmail: candidate.email.trim(),
        experience: candidate.experience.trim()
      });

      const activeUser = user || auth.currentUser;
      setCandidateUser(activeUser);

      setInterviewId(invite.interviewId);
      const baseConfig = {
        mode: "JOB",
        role: job.title,
        years_experience: candidate.experience.trim(),
        difficulty: "Intermediate",
        job_id: job.jobId,
        job_description: "",
        b2b_interview_mode: job.mode || JOB_MODES.RANDOM,
        uploaded_questions: job.uploadedQuestions || [],
        candidate_name: candidate.name.trim(),
        candidate_email: candidate.email.trim(),
        invite_interview_id: invite.interviewId
      };

      setShowInstructions(true);
      prepareInterviewSession(baseConfig, activeUser);
    } catch (err) {
      console.error("Failed to start invite interview:", err);
      setError(err.message || "Unable to start interview.");
    } finally {
      setStarting(false);
    }
  };

  const updateInviteStatus = async (status, { sessionId } = {}) => {
    if (!interviewId) {
      return;
    }
    try {
      await updateInviteInterviewStatus(interviewId, {
        status,
        sessionId
      });
    } catch (err) {
      console.warn("Unable to update invite interview status:", err);
    }
  };

  if (interviewStarted && interviewConfig) {
    return (
      <div className="app-container">
        <AppBackground />
        <AppHeader
          title="Vagmee"
          branding={branding}
          tagline={jobTagline(job)}
          actions={
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          }
        />
        <RealtimeConversation
          apiBase={API_BASE}
          interviewConfig={interviewConfig}
          user={candidateUser || auth.currentUser}
          onInterviewStarted={(payload) => updateInviteStatus("IN_PROGRESS", payload)}
          onInterviewComplete={(payload) => updateInviteStatus("COMPLETED", payload)}
          onInterviewStopped={(payload) => updateInviteStatus("STOPPED", payload)}
          onLogout={handleLogout}
          disableReportDownload
        />
      </div>
    );
  }

  return (
    <div className="app-container">
      <AppBackground />
      <AppHeader
        title="Vagmee"
        branding={branding}
        tagline={job ? jobTagline(job) : "Candidate interview"}
        actions={
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        }
      />

      {loading ? (
        <div className="loading-screen">
          <p>Loading interview...</p>
        </div>
      ) : error && !job ? (
        <div className="loading-screen">
          <p>{error}</p>
        </div>
      ) : !isCandidateAccount ? (
        <main className="candidate-invite-page">
          <section className="candidate-invite-panel single-column">
            <div className="candidate-invite-copy">
              <p className="candidate-kicker">Candidate Interview</p>
              <h1>
                <span>{job.title}</span>
                <strong>{job.companyName}</strong>
              </h1>
              <p>
                This interview link is only for candidate accounts. You are currently
                signed in as a company/recruiter account.
              </p>
              <div className="candidate-guidance">
                <span>Please log out and sign in with the candidate email to take this interview.</span>
              </div>
              <button className="primary-button" type="button" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </section>
        </main>
      ) : job.eventType === "MCQ" ? (
        <div className="assessment-page-body">
          <CompanyMcqTest jobId={jobId} />
        </div>
      ) : job.eventType === "CODING" ? (
        <div className="assessment-page-body">
          <CompanyCodingTest jobId={jobId} />
        </div>
      ) : (
        <main className="candidate-invite-page">
          <section className="candidate-invite-panel">
            <div className="candidate-invite-copy">
              <p className="candidate-kicker">Candidate Interview</p>
              <h1>
                <span>{job.title}</span>
                <strong>{job.companyName}</strong>
              </h1>
              <p>
                This interview is for the <strong>{job.title}</strong> profile at{" "}
                <strong>{job.companyName}</strong>.
                Use your own experience and judgment. Do not use AI tools,
                plagiarism, copied answers, or outside help during the interview.
              </p>
              <div className="candidate-guidance">
                <span>Speak clearly and keep your microphone available</span>
                <span>By pressing Start Interview, you give permission to record and proctor your video during the interview.</span>
                <span>All the best. Take a breath and answer naturally.</span>
              </div>
            </div>

            {showInstructions ? (
              <div className="candidate-invite-form candidate-instruction-card">
                <h2>Instructions</h2>
                {error && <div className="job-form-error">{error}</div>}
                <p>
                  You are signed in for the <strong>{job.title}</strong> interview at{" "}
                  <strong>{job.companyName}</strong>. The interview will be recorded
                  and proctored once you start.
                </p>
                <div className="candidate-guidance compact">
                  <span>Keep your camera and microphone available.</span>
                  <span>Answer in your own words without AI tools or outside help.</span>
                  <span>Speak clearly and pause when your answer is complete.</span>
                </div>
                {prepareStatus && <p className="candidate-prepare-status">{prepareStatus}</p>}
                <button className="primary-button" type="button" disabled>
                  {preparingGreeting || !interviewConfig ? "Preparing..." : "Starting..."}
                </button>
              </div>
            ) : (
              <form className="candidate-invite-form" onSubmit={startInterview}>
                <h2>Candidate Login</h2>
                {error && <div className="job-form-error">{error}</div>}

                <label>
                  <span>Name</span>
                  <input
                    value={candidate.name}
                    onChange={(event) => updateCandidate("name", event.target.value)}
                    placeholder="Your full name"
                  />
                </label>

                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    value={candidate.email}
                    onChange={(event) => updateCandidate("email", event.target.value)}
                    placeholder="you@example.com"
                    readOnly={!!user?.email}
                  />
                </label>

                <label>
                  <span>Experience</span>
                  <input
                    value={candidate.experience}
                    onChange={(event) => updateCandidate("experience", event.target.value)}
                    placeholder="e.g., 3 years"
                  />
                </label>

                <button className="primary-button" type="submit" disabled={starting}>
                  {starting ? "Continuing..." : "Continue"}
                </button>
              </form>
            )}
          </section>
        </main>
      )}
    </div>
  );
}
